import { useTranslation } from 'react-i18next';
import type { DriverDocumentKey, DriverDocuments } from '@/types';
import { DRIVER_DOCUMENT_KEYS } from '@/types';

interface DriverDocumentsSectionProps {
  documents: DriverDocuments;
  onView: (key: DriverDocumentKey) => void;
}

const DOCUMENT_LABEL_KEY: Record<DriverDocumentKey, string> = {
  nationalId: 'drivers.form.nationalId',
  license: 'drivers.form.license',
  vehicle: 'drivers.form.vehicle',
};

const DOCUMENT_ICON: Record<DriverDocumentKey, string> = {
  nationalId: 'ri-id-card-line',
  license: 'ri-roadster-line',
  vehicle: 'ri-truck-line',
};

function isImage(url: string | null): boolean {
  if (!url) return false;
  return url.startsWith('data:image') || /\.(png|jpe?g|webp|gif)$/i.test(url) || url.includes('readdy.ai');
}

export default function DriverDocumentsSection({ documents, onView }: DriverDocumentsSectionProps) {
  const { t } = useTranslation();

  return (
    <ul className="divide-y divide-background-100">
      {DRIVER_DOCUMENT_KEYS.map((key) => {
        const doc = documents[key];
        const thumb = doc && isImage(doc.url) ? doc.url : null;
        return (
          <li key={key} className="flex flex-wrap items-center gap-3 px-4 py-3.5 md:px-5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-200 bg-background-100 text-foreground-500">
              {thumb ? (
                <img src={thumb} alt={t(DOCUMENT_LABEL_KEY[key])} className="h-full w-full object-cover" />
              ) : (
                <i className={`${DOCUMENT_ICON[key]} text-lg`} aria-hidden="true" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground-900">{t(DOCUMENT_LABEL_KEY[key])}</p>
              <p className="lb-ltr-field mt-0.5 truncate text-xs text-foreground-500">
                {doc ? doc.fileName : t('drivers.detail.document.none')}
              </p>
            </div>
            {doc ? (
              <button
                type="button"
                onClick={() => onView(key)}
                className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-eye-line text-base" aria-hidden="true" />
                {t('drivers.detail.document.view')}
              </button>
            ) : (
              <span className="text-xs text-foreground-400">{t('drivers.detail.document.none')}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}